import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ContactSearch from "./ContactSearch";
import { Plus, Upload, Download, ArrowUpDown } from "lucide-react";
import MoreFilter, { type ContactFilters } from "../ui/shared/MoreFilter";
import AddNewContact from "./AddNewContact";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import type { Database } from "@/lib/supabase";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { getDisplayName, getContactPhoneForExport } from "@/utils/contactUtils";
import { applyChatContactScopeFilter } from "@/lib/chatContactScope";
import * as XLSX from 'xlsx';
import { formatDateInAccountTz } from '@/lib/accountTimezone';
import { useTranslation } from "react-i18next";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

export type SortOption = 'created_desc' | 'created_asc' | 'name_asc' | 'name_desc';

interface FilterHeaderProps {
  onSearchChange?: (searchTerm: string) => void;
  searchTerm?: string;
  currentFilters: ContactFilters;
  onFilterChange: (filters: ContactFilters) => void;
  onClearFilters: () => void;
  sortBy?: SortOption;
  onSortChange?: (sort: SortOption) => void;
}

const FilterHeader: React.FC<FilterHeaderProps> = ({
  onSearchChange,
  searchTerm = "",
  currentFilters,
  onFilterChange,
  onClearFilters,
  sortBy = 'created_desc',
  onSortChange
}) => {
  const { t } = useTranslation("crm");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);

  const handleContactSelect = (contact: AccountContact) => {
    console.log('🎯 [FilterHeader] Contato selecionado na busca:', contact);
  };

  const handleImportClick = () => {
    navigate('/contacts/importar');
  };

  const handleExportClick = async () => {
    if (!user?.account_id || exporting || !supabase) return;

    setExporting(true);

    try {
      console.log('📤 [FilterHeader] Iniciando exportação com filtros:', {
        searchTerm,
        filters: currentFilters
      });

      let query = supabase
        .from('account_contacts')
        .select(`
          name,
          display_name,
          push_name_last,
          jid,
          email,
          phone,
          tags,
          campaigns,
          created_at,
          account_assistant_id,
          account_assistants!account_contacts_account_assistant_id_fkey (
            name
          )
        `)
        .eq('account_id', user.account_id)
        .eq('is_group', false);

      query = applyChatContactScopeFilter(query, user);

      if (currentFilters.assistantId && currentFilters.assistantId !== '__clear__') {
        query = query.eq('account_assistant_id', currentFilters.assistantId);
      }

      if (currentFilters.attributionId && currentFilters.attributionId !== '__clear__') {
        if (currentFilters.attributionId === '__unassigned__') {
          query = query.is('attribution_id', null);
        } else {
          query = query.eq('attribution_id', currentFilters.attributionId);
        }
      }

      if (currentFilters.tags.length > 0 && currentFilters.tags[0] !== '__clear__') {
        query = query.contains('tags', currentFilters.tags.map(t => t.toLowerCase()));
      }

      if (currentFilters.campaigns.length > 0 && currentFilters.campaigns[0] !== '__clear__') {
        query = query.contains('campaigns', currentFilters.campaigns);
      }

      if (currentFilters.dateFrom) {
        const fromDate = new Date(currentFilters.dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        query = query.gte('created_at', fromDate.toISOString());
      }

      if (currentFilters.dateTo) {
        const toDate = new Date(currentFilters.dateTo);
        toDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', toDate.toISOString());
      }

      const { data: contacts, error } = await query.order('name', { ascending: true });

      if (error) {
        console.error('Erro ao buscar contatos:', error);
        alert(t("list.exportError"));
        return;
      }

      if (!contacts || contacts.length === 0) {
        alert(t("list.exportEmpty"));
        return;
      }

      let exportContacts = contacts;

      if (searchTerm && searchTerm.trim()) {
        const search = searchTerm.toLowerCase();
        exportContacts = exportContacts.filter((contact: any) => {
          const displayName = getDisplayName(contact).toLowerCase();
          const phone = getContactPhoneForExport(contact).toLowerCase();
          const email = (contact.email || '').toLowerCase();

          return displayName.includes(search) ||
                 phone.includes(search) ||
                 email.includes(search) ||
                 contact.jid.includes(search);
        });
      }

      if (exportContacts.length === 0) {
        alert(t("list.exportNoMatch"));
        return;
      }

      console.log('✅ [FilterHeader] Exportando contatos:', {
        total: exportContacts.length
      });

      const exportData = exportContacts.map((contact: any) => ({
        [t('export.name')]: getDisplayName(contact),
        [t('export.phone')]: getContactPhoneForExport(contact),
        [t('export.email')]: contact.email || '',
        [t('export.assistant')]: contact.account_assistants?.name || '',
        [t('export.tags')]: Array.isArray(contact.tags) ? contact.tags.join(', ') : '',
        [t('export.campaigns')]: Array.isArray(contact.campaigns) ? contact.campaigns.join(', ') : '',
        [t('export.createdAt')]: contact.created_at
          ? formatDateInAccountTz(contact.created_at)
          : ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 30 },
        { wch: 18 },
        { wch: 35 },
        { wch: 25 },
        { wch: 30 },
        { wch: 30 },
        { wch: 18 }
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Contatos');

      const hasFilters =
        currentFilters.assistantId ||
        currentFilters.attributionId ||
        currentFilters.tags.length > 0 ||
        currentFilters.campaigns.length > 0 ||
        currentFilters.dateFrom ||
        currentFilters.dateTo ||
        searchTerm?.trim();

      const fileName = hasFilters
        ? `contatos_filtrados_${new Date().toISOString().split('T')[0]}.xlsx`
        : `contatos_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, fileName);

    } catch (error) {
      console.error('Erro ao exportar contatos:', error);
      alert(t("list.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  const secondaryBtn =
    "h-12 px-4 rounded-[12px] border border-border bg-card text-foreground font-medium text-[14px] flex justify-center items-center gap-2 shadow-sm transition-colors duration-200 hover:bg-background disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="w-full flex-wrap gap-3 flex justify-between items-center">
      <div className="flex items-center gap-3 flex-wrap">
        <ContactSearch
          onContactSelect={handleContactSelect}
          onSearchChange={onSearchChange}
          placeholder={t("list.searchPlaceholder")}
          styles="w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] h-12"
        />
        <MoreFilter
          currentFilters={currentFilters}
          onFilterChange={onFilterChange}
          onClearFilters={onClearFilters}
        />
        {onSortChange && (
          <Select
            value={sortBy}
            onValueChange={(value) => onSortChange(value as SortOption)}
          >
            <SelectTrigger className="!w-[180px] !h-12 !min-h-12 rounded-[12px] bg-card border-border text-foreground shadow-sm hover:bg-background transition-colors duration-200">
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                <SelectValue />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="created_desc">{t("list.newest")}</SelectItem>
                <SelectItem value="created_asc">{t("list.oldest")}</SelectItem>
                <SelectItem value="name_asc">{t("list.nameAsc")}</SelectItem>
                <SelectItem value="name_desc">{t("list.nameDesc")}</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <AddNewContact />
        <button
          onClick={handleExportClick}
          disabled={exporting}
          className={secondaryBtn}
        >
          <Download className="w-4 h-4 text-muted-foreground" />
          {exporting ? "Exportando..." : "Exportar"}
        </button>
        <button onClick={handleImportClick} className={secondaryBtn}>
          <Upload className="w-4 h-4 text-muted-foreground" />
          {t("list.import")}
        </button>
      </div>
    </div>
  );
};

export default FilterHeader;
